import React, { useMemo } from "react";
import TableServerBase from "@/components/tables/TableServer";
import { getFormatDate } from "@/helpers/utils";
import { CurrencyFormateText } from "@/components/CurrencyFormat";
import DownloadUserSubscriptionDetail from "./DownloadUserSubscriptionsDetail";
import { MUICustomAutocompleteSelect } from "@/components/muiCustom/select/MUICustomAutocompleteSelect";
import StatusChip from "@/components/muiCustom/chip/StatusChip";
import { SUBSCRIPTION_CONST } from "@/config/constants";
import PageHeader from "@/components/layouts/pageHeaders/PageHeader";

const TableServer = TableServerBase as any;

const MEDIA_PLAN_STATUS = {
  1: {
    label: SUBSCRIPTION_CONST.STATUS.SUBSCRIBED.TEXT,
    chipStatus: SUBSCRIPTION_CONST.STATUS.SUBSCRIBED.TYPE,
  },
  0: {
    label: SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TEXT,
    chipStatus: SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TYPE,
  },
  4: {
    label: SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TEXT,
    chipStatus: SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TYPE,
  },
};

const ADVERTISING_STATUS = {
  1: {
    label: SUBSCRIPTION_CONST.STATUS.ACTIVE.TEXT,
    chipStatus: SUBSCRIPTION_CONST.STATUS.ACTIVE.TYPE,
  },
  0: {
    label: SUBSCRIPTION_CONST.STATUS.INACTIVE.TEXT,
    chipStatus: SUBSCRIPTION_CONST.STATUS.INACTIVE.TYPE,
  },
};

const LOCATION_STATUS = {
  1: {
    label: "Open",
    chipStatus: SUBSCRIPTION_CONST.STATUS.ACTIVE.TYPE,
  },
  2: {
    label: "Temporarily Closed",
    chipStatus: SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TYPE,
  },
  3: {
    label: "Closed",
    chipStatus: SUBSCRIPTION_CONST.STATUS.INACTIVE.TYPE,
  },
};

type StatusConfig = {
  chipStatus: string;
  label: string;
};

type StatusMap = Record<number, StatusConfig>;

type TableCellProps = {
  row: {
    original: Record<string, any>;
  };
};

type UserSubscriptionsProps = {
  fetchData: (_data: Record<string, any>) => void;
  filtersData: Record<string, any>;
  groupList?: Array<Record<string, any>>;
  is_brand_auto_renew_on?: boolean;
  isLoading?: boolean;
  locationData?: Record<string, any>;
};

const getStatusConfig = (statusMap: StatusMap, value: any) =>
  statusMap[Number(value)];

const renderStatusChip = (statusMap: StatusMap, value: any) => {
  const statusConfig = getStatusConfig(statusMap, value);

  if (!statusConfig) return "N/A";

  return (
    <StatusChip label={statusConfig.label} status={statusConfig.chipStatus} />
  );
};

export default function UserSubscriptions({
  isLoading,
  locationData,
  groupList = [],
  fetchData,
  filtersData,
  is_brand_auto_renew_on,
}: UserSubscriptionsProps) {
  const { locations = [], totalLocationCount = 0 } = locationData || {};

  const pageCount = useMemo(
    () => Math.ceil(totalLocationCount / 10) || 0,
    [totalLocationCount]
  );

  const columns = useMemo(() => {
    const allColumns = [
      {
        Header: "Location",
        accessor: "location_name",
        Cell: ({ row: { original } }: TableCellProps) => (
          <span className="text-wrap user-select-all fw-b">
            {original.location_name || "N/A"}
          </span>
        ),
      },
      {
        Header: "Participant",
        accessor: "participant_name",
        Cell: ({ row: { original } }: TableCellProps) => (
          <span className="text-wrap user-select-all">
            {original.participant_name || "N/A"}
          </span>
        ),
      },
      {
        Header: "Media Plan Start",
        accessor: "current_subscription_start_date",
        width: 120,
        Cell: ({ row: { original } }: TableCellProps) =>
          original.current_subscription_start_date || "N/A",
      },
      {
        Header: "Media Plan End",
        accessor: "current_subscription_end_date",
        width: 115,
        Cell: ({ row: { original } }: TableCellProps) =>
          original.current_subscription_end_date || "N/A",
      },
      {
        Header: "Media Plan",
        accessor: "current_subscription_package",
        width: 130,
        Cell: ({ row: { original } }: TableCellProps) =>
          original.current_subscription_package || "N/A",
      },
      {
        Header: "Monthly Media",
        accessor: "monthly_package_media",
        width: 100,
        Cell: ({ row: { original } }: TableCellProps) =>
          original.monthly_package_media ? (
            <CurrencyFormateText value={original.monthly_package_media || 0} />
          ) : (
            "N/A"
          ),
      },
      {
        Header: "Upcoming Media Plan Start",
        accessor: "upcoming_subscription_start_date",
        width: 120,
        Cell: ({ row: { original } }: TableCellProps) =>
          original.upcoming_subscription_start_date || "N/A",
      },
      {
        Header: "Upcoming Media Plan End",
        accessor: "upcoming_subscription_end_date",
        width: 115,
        Cell: ({ row: { original } }: TableCellProps) =>
          original.upcoming_subscription_end_date || "N/A",
      },
      {
        Header: "Upcoming Media Plan",
        accessor: "upcoming_subscription_subscription_package",
        width: 130,
        Cell: ({ row: { original } }: TableCellProps) =>
          original.upcoming_subscription_subscription_package || "N/A",
      },
      {
        Header: "Location Status",
        accessor: "location_status",
        width: 100,
        Cell: ({ row: { original } }: TableCellProps) =>
          renderStatusChip(LOCATION_STATUS, original.location_status),
      },
      {
        Header: "Advertising Status",
        accessor: "advertising_status",
        width: 130,
        Cell: ({ row: { original } }: TableCellProps) =>
          renderStatusChip(ADVERTISING_STATUS, original.advertising_status),
      },
      {
        Header: "Media Plan Status",
        accessor: "media_plan_status",
        width: 130,
        Cell: ({ row: { original } }: TableCellProps) =>
          renderStatusChip(MEDIA_PLAN_STATUS, original.media_plan_status),
      },
      {
        Header: "Auto-Renew Status",
        accessor: "opt_out_flag",
        width: 130,
        Cell: ({ row: { original } }: TableCellProps) => {
          const { opt_out_flag } = original;
          return (
            <StatusChip
              label={
                opt_out_flag
                  ? SUBSCRIPTION_CONST.STATUS.AUTO_RENEW_OFF.TEXT
                  : SUBSCRIPTION_CONST.STATUS.AUTO_RENEW_ON.TEXT
              }
              status={
                opt_out_flag
                  ? SUBSCRIPTION_CONST.STATUS.AUTO_RENEW_OFF.TYPE
                  : SUBSCRIPTION_CONST.STATUS.AUTO_RENEW_ON.TYPE
              }
            />
          );
        },
      },
    ];
    if (!is_brand_auto_renew_on) {
      return allColumns.filter((column) => column.accessor !== "opt_out_flag");
    }
    return allColumns;
  }, [is_brand_auto_renew_on]);

  const data = useMemo(() => {
    return locations.map((l: Record<string, any>) => {
      const locData = { ...l };
      // formatting dates
      locData.current_subscription_start_date = getFormatDate(
        l.current_subscription_start_date
      );
      locData.current_subscription_end_date = getFormatDate(
        l.current_subscription_end_date
      );

      locData.upcoming_subscription_start_date = getFormatDate(
        l.upcoming_subscription_start_date
      );
      locData.upcoming_subscription_end_date = getFormatDate(
        l.upcoming_subscription_end_date
      );

      locData.locUniqId = l.defined_unique_id
        ? l.defined_unique_id
        : l.system_generated_location_id;

      return locData;
    });
  }, [locations]);

  const subscriptionStatusOptions = useMemo(() => {
    return [
      { label: SUBSCRIPTION_CONST.STATUS.SUBSCRIBED.TEXT, value: 1 },
      {
        label: SUBSCRIPTION_CONST.STATUS.UNSUBSCRIBED.TEXT,
        value: 0,
      },
    ];
  }, []);

  const subscriptionLengthOptions = useMemo(() => {
    return SUBSCRIPTION_CONST.SUBSCRIPTION_LENGTH_OPTIONS.map((o) => ({
      label: o.LABEL,
      value: o.VALUE,
    }));
  }, []);

  const filters = (
    setFilter: (_key: string, _value: any) => void,
    selectedValues: Record<string, any>
  ) => {
    // Should be defined in tha parent, but for the time being defining here
    return (
      <>
        <div className="col-xl col-lg-8 px-4 px-xl-1 pt-2 px-lg-auto mb-md-2 mb-lg-0 d-flex align-items-end justify-content-end d-lg-none">
          {locationData?.locations?.length ? (
            <DownloadUserSubscriptionDetail filters={filtersData} />
          ) : null}
        </div>
        <div className="col-xl col-lg-4 pr-xl-1 mt-2">
          <MUICustomAutocompleteSelect
            options={groupList?.map((g: Record<string, any>) => ({
              label: g.group_name,
              value: g.id,
            }))}
            selectProps={{
              onChange: (v: any) => {
                setFilter("groupKey", v);
              },
              value: selectedValues.groupKey,
            }}
            name="location-group"
            label="Groups"
            all
          />
        </div>
        <div className="col-xl col-lg-4 px-xl-1 mt-2">
          <MUICustomAutocompleteSelect
            options={subscriptionStatusOptions}
            selectProps={{
              onChange: (v: any) => {
                setFilter("statusKey", v);
              },
              value: selectedValues.statusKey,
            }}
            name="location-status"
            label="Media Plan Status"
            all
          />
        </div>
        <div className="col-xl col-lg-4 px-xl-1 mt-2">
          <MUICustomAutocompleteSelect
            options={subscriptionLengthOptions}
            selectProps={{
              onChange: (v: any) => {
                setFilter("subsLenKey", v);
              },
              value: selectedValues.subsLenKey,
            }}
            name="location-subscription-length"
            label="Length of Plan"
            all
          />
        </div>
        <div className="col-xl col-lg-9 px-4 px-xl-1 px-lg-auto mb-md-2 d-lg-flex align-items-end d-none mt-3">
          {locationData?.locations?.length ? (
            <DownloadUserSubscriptionDetail filters={filtersData} />
          ) : null}
        </div>
      </>
    );
  };

  return (
    <>
      <div className="row align-items-center mb-4 ml-1">
        <PageHeader title="Your Plan" showHr={false} />
      </div>

      <div>
        <TableServer
          columns={columns}
          data={data}
          showSearch={true}
          showFilter
          filters={filters}
          fetchData={fetchData}
          isLoading={isLoading}
          pageCount={pageCount}
          totalRecords={
            locationData?.totalLocationCount ? totalLocationCount : 0
          }
        />
      </div>
    </>
  );
}
